import { createAsyncThunk, createSlice, PayloadAction, Dispatch } from "@reduxjs/toolkit";
import axios from "axios";
import apiUrls from "../../../api";
import { IAppLoaderAction } from "../../../common/state/loaderHandleMiddleware";
import { RootState } from "../../../common/state/store";
import { IMedia, ITagConfig } from "../../../model/unityObject";
import { Field } from "../../../features/graph/util/graphFieldsExtractor";
import { getMediaLabelValue, getMediaTypeValue, getTagsOrganizationMedia } from "./filterUtil";
import { cUPP } from "./uploadUtil";
import { uuidv4 } from "src/common/util/uuidv4";
import { getUserPreferences } from "src/common/util/getUserPreferences";
import { t } from "i18next";
import { setAlert } from "src/features/alert/state/alertsSlice";

export const HARDCODED_ELEVENLABS_VOICE_ID = {
  voiceId: "pFZP5JQG7iQjIQuC4Bku",
  ttsProvider: "elevenLabs",
};

type FetchFilteredMediaArgs = Partial<IMediaSearch> &
  IAppLoaderAction & {
    start?: number;
    selectedId?: number;
  };

export interface IMediaDraft {
  name: string;
  s3Url: string;
  mimeType: string;
  media360tag: string;
  fileSizeInByte: number;
  fileName: string;
  MD5: string;
  upload_status: string;
  tags: [] | ITagUpload[];
}

export interface IBlockadeLabsModel {
  id: number;
  name: string;
  "max-char": number;
  image: string;
}

export interface IBlockadeLabsPreview {
  thumbnailUrl: string;
  imageUrl: string;
  faultyImageId?: string;
  blockadeLabImageId?: number;
  errorMessage?: string;
  id?: number;
}

export interface IBlockadeLabsStore {
  models: IBlockadeLabsModel[];
  preview?: IBlockadeLabsPreview | null;
}

export interface IElevenlabsVoice {
  id: string;
  name: string;
  language: string;
  gender: string;
  tts_provider: string;
  // ElevenLabs community copies can be disabled by their owner at any time
  is_community_voice?: boolean;
}

export interface ITtsPreview {
  url: string;
}

interface IMultipartUploadParams {
  file: File;
  orgId: number;
  cb?: any;
  media_360_tag: string;
  name: string;
  MD5: string;
  metadata?: Record<string, unknown>;
}

interface IPutChunkParams {
  uploadId: string;
  name: string;
  S3filename: string;
  media360tag: string;
  MD5: string;
  partNumber: number;
  file: File;
  chunkBegining: number;
  chunkEnd: number;
  unsuccessfulTry: number;
  cb: any;
  dispatch?: Dispatch;
  getStore?: any;
  metadata?: Record<string, unknown>;
}

interface ITagUpload {
  mediaId: number;
  orgaId: number;
  tags: ITagConfig[];
}

export interface IUploadAvatarParams {
  name: string;
  avatarUrl: string;
  voices: IUploadAvatarVoiceParams[];
}

export interface IUploadAvatarVoiceParams {
  voiceId: string;
  lang: string;
  gender: string;
  prettyVoiceName: string;
  tts_provider: string;
}
export interface IMediaSearchIndex {
  id: number;
  name: string;
  type: string;
  media_360_tag: string;
  created_at: string;
  organizationId: number;
}

export interface IMediaUpload {
  name: string;
  size_in_bytes: number;
  type: string;
  media_360_tag: string;
  MD5: string;
}

interface UploadStateConfigTags {
  newTags: Array<any>;
  oldTags: Array<any>;
}

interface ISUFPayload {
  file: File;
  metadata: cUPP;
}

export interface IMediaSearch {
  limit?: number;
  page: number;
  query?: string;
  selectedId?: string | number;
  sort: string;
  media360tag: string;
  mediaType: string;
  owner: string;
  projectGroupId?: number;
  projectId?: number;
}

export interface IDeleteTagsInAllMedias {
  dataTags: ITagConfig[];
  idSourceMedia: number;
  allMedia: IMedia[];
}

export type UploadState =
  | "in_progress"
  | "completed"
  | "pause"
  | "canceled"
  | "error"
  | "transcoding"
  | "transcribing";
export interface IUploadItem {
  uploadId: string;
  name?: string;
  s3name?: string;
  percent?: number;
  errorMessage?: string | null;
  isTranscoding?: boolean;
  size?: number;
  uploadState?: UploadState;
  uploadData?: IPutChunkParams | null;
}

export interface IUploadState {
  currentUploadsStateList: Array<IUploadItem>;
}
export interface IUploadTagsForDisableButton {
  id: any;
  text: any;
}

export interface IUpdatedWithPlaceholder {
  id: number;
  modelName: string;
  keyName: string;
  mediaType: string;
  newMediaId: number;
}

export interface IDeleteMediaResponse {
  deletedMediaId: number;
  updatedWithPlaceholders: IUpdatedWithPlaceholder[];
}

export interface IBlockadeLabsPreviewPayload {
  modelId: number;
  prompt: string;
  imagePrompt?: string;
  initStrength?: number;
  // "2d": imagePrompt is a regular 2D photo; the server drafts a 360 via GPT
  // before the Blockade upscale (async, completion via SSE 331/332)
  sourceMode?: "2d";
}

export interface IMediaState {
  all: Array<IMedia>;
  filtered: Array<IMedia>;
  config: {
    tags: Array<ITagConfig>;
    mediaType: Array<string>;
    labels: Array<string>;
  };
  shouldResetFilters: boolean;
  activeArrowFilterList: string;
  currentEditedMediaField: Field;
  currentMediaSidebar: number;
  currentMediaGallery: number;
  originalMediaGallery: number;
  isUploading: boolean;
  uploadedFile?: File;
  uploadedFileMetadata?: cUPP;
  uploadedOldFile?: File;
  uploadedOldFileMetadata?: cUPP;
  currentUploadsStateList: Array<IUploadItem>;
  openedSlider: string;
  isGalleryLoaded: boolean;
  // Number of `fetchFilteredMedias` requests currently in flight. The grid
  // renders `filtered`, which only this thunk fills — `isGalleryLoaded` tracks
  // `getAllMedia` instead, so it can be true while a filtered fetch is still
  // running (empty list → false "no results" flash). A counter (not a boolean)
  // survives overlapping fetches (mount + filter + search debounce).
  filteredMediasInFlight: number;
  playingMedia: number;
  warningMediaModules: Array<any>;
  currentModeListGallery: boolean;
  activeButtonModalGalleryConfirm: boolean;
  ttsPreview?: ITtsPreview;
  elevenlabsVoices: IElevenlabsVoice[];
  blockadeLabs: IBlockadeLabsStore;
  search: IMediaSearch;
  searchIndex: IMediaSearchIndex[];
  numberOfSearchHits: number;
}

const initialState: IMediaState = {
  all: [],
  filtered: [],
  config: { tags: [], mediaType: [], labels: [] },
  shouldResetFilters: false,
  activeArrowFilterList: "",
  currentEditedMediaField: { fieldName: "", currentValue: 0, type: "media", mediaType: "image" },
  currentMediaSidebar: 0,
  currentMediaGallery: 0,
  originalMediaGallery: 0,
  isUploading: false,
  currentUploadsStateList: [],
  openedSlider: "",
  isGalleryLoaded: false,
  filteredMediasInFlight: 0,
  playingMedia: 0,
  warningMediaModules: [],
  currentModeListGallery: false,
  activeButtonModalGalleryConfirm: true,
  ttsPreview: null,
  blockadeLabs: {
    models: [],
    preview: undefined,
  },
  elevenlabsVoices: [],
  searchIndex: [],
  search: {
    limit: 20,
    page: 0,
    query: "",
    selectedId: "",
    sort: "updated_at_desc",
    media360tag: "",
    mediaType: "all",
    owner: "all",
    projectGroupId: undefined,
    projectId: undefined,
  },
  numberOfSearchHits: 20,
};

type SearchKey = keyof IMediaSearch;
type SearchValue<K extends SearchKey = SearchKey> = IMediaSearch[K];
type SearchUpdatePayload = { key: SearchKey; value: SearchValue } | Partial<IMediaSearch>;
type SingleSearchUpdate<K extends SearchKey = SearchKey> = {
  key: K;
  value: SearchValue<K>;
};

const isSingleSearchUpdate = (payload: SearchUpdatePayload): payload is SingleSearchUpdate => {
  return (
    typeof payload === "object" &&
    payload !== null &&
    Object.prototype.hasOwnProperty.call(payload, "key") &&
    Object.prototype.hasOwnProperty.call(payload, "value")
  );
};

export const fetchMediaSearchIndex = createAsyncThunk(
  "media/fetchSearchIndex",
  async ({
    projectId,
    projectGroupId,
  }: { projectId?: number; projectGroupId?: number } & IAppLoaderAction) => {
    const params: Record<string, any> = {};

    if (projectId) {
      params.projectId = projectId;
    } else if (projectGroupId) {
      params.projectGroupId = projectGroupId;
    }

    const res = await axios.get(apiUrls.medias.searchIndex, { params });
    return res.data;
  },
);

export const getAllMedia = createAsyncThunk(
  "media/all",
  async ({ orgId }: { orgId: number } & IAppLoaderAction) => {
    const allMediaUrl = `${apiUrls.medias.all}?filters[organization][id][$in][0]=${orgId}&filters[organization][id][$in][1]=3&populate[0]=organization&populate[1]=source_media_cache`;
    const tagsOrganizationUrl = `${apiUrls.medias.getTagsOrganization}?populate[organization_id]=*`;

    // Parallelize requests using Promise.all
    const [response, responseTagsOrganization] = await Promise.all([
      axios.get<Array<IMedia>>(allMediaUrl),
      axios.get(tagsOrganizationUrl),
    ]);

    const media = response.data.map((media) => {
      return {
        ...media,
      };
    });

    // Return structured data from both requests
    return {
      data: media,
      orgId: orgId,
      tagsOrganization: responseTagsOrganization.data,
    };
  },
);

export const getMedia = createAsyncThunk(
  "media/one",
  async ({ mediaId }: { mediaId: number } & IAppLoaderAction) => {
    return await axios
      .get(
        `${apiUrls.medias.all}/${mediaId}?populate[0]=organization&populate[1]=source_media_cache`,
      )
      .then((res) => res.data);
  },
);

export const fetchFilteredMedias = createAsyncThunk(
  "media/fetchFilteredMedias",
  async (
    {
      selectedId,
      projectGroupId,
      projectId,
      start = 0,
      limit,
      media360tag,
      mediaType,
      owner,
      sort,
      query,
    }: FetchFilteredMediaArgs,
    thunkAPI,
  ) => {
    const state = thunkAPI.getState() as RootState;
    const mediaSearch = state.media.search;

    const params = new URLSearchParams();

    // ✅ Merge overrides with Redux state
    const effectiveLimit = limit ?? mediaSearch.limit ?? 30;
    const effectiveSort = sort ?? mediaSearch.sort;
    const effectiveMediaType = mediaType ?? mediaSearch.mediaType;
    const effectiveMedia360tag = media360tag ?? mediaSearch.media360tag;
    const effectiveOwner = owner ?? mediaSearch.owner;
    const effectiveQuery = query ?? mediaSearch.query;
    const effectiveProjectGroupId = projectGroupId ?? mediaSearch.projectGroupId;
    const effectiveProjectId = projectId ?? mediaSearch.projectId;

    params.append("limit", effectiveLimit.toString());

    if (selectedId !== undefined) {
      params.append("selectedId", String(selectedId));
    }

    if (start) {
      params.append("start", start.toString());
    }

    if (effectiveMedia360tag) {
      params.append("media360tag", effectiveMedia360tag);
    }
    if (effectiveMediaType) {
      params.append("mediaType", effectiveMediaType);
    }
    if (effectiveOwner) {
      params.append("owner", effectiveOwner);
    }
    if (effectiveSort) {
      params.append("sort", effectiveSort);
    }
    if (effectiveQuery) {
      params.append("query", effectiveQuery);
    }
    if (effectiveProjectGroupId) {
      params.append("projectGroupId", String(effectiveProjectGroupId));
    }
    if (effectiveProjectId) {
      params.append("projectId", String(effectiveProjectId));
    }

    const url = `${apiUrls.medias.all}/filtered${params.toString() ? "?" + params.toString() : ""}`;

    const response = await axios.get(url);

    return { data: response.data };
  },
);

export const deleteMedia = createAsyncThunk(
  "media/delete",
  async ({ mediaId }: { mediaId: IMedia["id"] } & IAppLoaderAction) => {
    return await axios
      .delete(`${apiUrls.medias.all}/${mediaId}`)
      .then((res) => res.data as IDeleteMediaResponse);
  },
);

export const deleteMultipleMediaFromIds = createAsyncThunk(
  "media/deleteMultipleMediaFromIds",
  async ({ mediaIds }: { mediaIds: Array<[]> } & IAppLoaderAction) => {
    return await axios
      .delete(`${apiUrls.medias.deleteMultipleMedias}?mediaIds=${mediaIds.toString()}`)
      .then((res) => res.data);
  },
);

export const checkIfMediasAreUsed = createAsyncThunk(
  "media/checkIfMediasAreUsed",
  async ({
    mediaIDs,
    isToDeleteOne = false,
  }: { mediaIDs: Array<IMedia>; isToDeleteOne: boolean } & IAppLoaderAction) => {
    const response = axios.post(apiUrls.medias.checkIfMediasAreUsed, {
      data: { mediaIDs, isToDeleteOne },
    });
    return { data: (await response).data };
  },
);
export const updateMediaDetail = createAsyncThunk(
  "media/update",
  async ({
    media,
    metadata,
  }: { media: IMedia; metadata?: Record<string, unknown> } & IAppLoaderAction) => {
    const updateData: Record<string, unknown> = {
      name: media.name,
      media_360_tag: media.media_360_tag,
    };
    if (metadata !== undefined) {
      updateData.metadata = metadata;
    }
    await axios.put(`${apiUrls.medias.all}/${media.id}`, updateData);
    return media;
  },
);

export const fetchElevenlabsVoices = createAsyncThunk(
  "media/fetchElevenlabsVoices",
  // eslint-disable-next-line no-empty-pattern
  async ({}: Record<string, any> & IAppLoaderAction) => {
    return await axios.get(`${apiUrls.medias.elevenLabs.fetchVoices}`).then((res) => {
      console.log("TESTV => ", res.data);
      return res.data;
    });
  },
);

export const generateVoiceOver = createAsyncThunk<
  ArrayBuffer,
  {
    voice: string;
    ttsProvider: string;
    language?: string; // was used with multilingual_v2_5_turbo model; not anymo'e
    text: string;
    tags?: any[];
    voiceSettings?: {
      stability?: number;
      similarity_boost?: number;
    };
  } & IAppLoaderAction
>(
  "media/generateVoiceOver",
  async ({ voice, ttsProvider, language = null, text, tags, voiceSettings }, thunkAPI) => {
    const response = await axios.post(
      apiUrls.medias.generateAudio,
      {
        ...(language ? { language } : {}),
        voice,
        ttsProvider,
        text,
        tags,
        ...(voiceSettings ? { voiceSettings } : {}),
      },
      {
        timeout: 300000,
        responseType: "arraybuffer",
      },
    );

    // The backend transparently swaps an unavailable (owner-disabled) voice for the
    // default narration voice and flags it via this header. Let the user know.
    const fallbackVoiceName = response.headers?.["x-tts-voice-fallback"];
    if (fallbackVoiceName) {
      thunkAPI.dispatch(
        setAlert({
          type: "warning",
          msg: t("pages.gallery.generateVoicePanel.voiceFallbackNotice", {
            voice: fallbackVoiceName,
          }),
        }),
      );
    }

    return response.data;
  },
);

/* UPLOAD STUFF STARTS HERE */
export const uploadAvatar = createAsyncThunk(
  "media/uploadAvatar",
  async ({ name, avatarUrl, voices }: IUploadAvatarParams & IAppLoaderAction) => {
    const params = {
      name,
      avatarUrl,
      voices,
    };

    const res = await axios.post(apiUrls.medias.uploadAvatar, { data: params });

    return res.data;
  },
);

export const updateAvatarVoices = createAsyncThunk(
  "media/updateAvatarVoices",
  async ({
    mediaId,
    voices,
  }: {
    mediaId: number;
    voices: IUploadAvatarVoiceParams[];
  } & IAppLoaderAction) => {
    const res = await axios.put(`${apiUrls.medias.updateAvatarVoices}/${mediaId}`, {
      data: { voices },
    });

    return res.data;
  },
);

export const duplicateAvatar = createAsyncThunk(
  "media/duplicateAvatar",
  async ({
    avatarId,
    name,
    voices,
  }: {
    avatarId: number;
    name: string;
    voices: IUploadAvatarVoiceParams[];
  } & IAppLoaderAction) => {
    const res = await axios.post(apiUrls.medias.duplicateAvatar, {
      data: { avatarId, name, voices },
    });

    return res.data;
  },
);

export const uploadMultipart =
  ({ file, orgId, cb, media_360_tag, name, MD5, metadata = {} }: IMultipartUploadParams): any =>
  (dispatch: Dispatch, getStore: any) => {
    const uploadLimitInBytes = 8e18;
    const chunkSizeInBytes = 10e6;

    if (file.size <= uploadLimitInBytes) {
      const tabTmp = file.name.split(".");
      const extension = tabTmp[tabTmp.length - 1];

      let fileType = file.type;

      if (window.navigator.userAgent.includes("Safari")) {
        if (
          fileType === "" &&
          (extension.toLowerCase() === "ogg" || extension.toLowerCase() === "oga")
        ) {
          fileType = "audio/" + extension.toLowerCase();
        }
      }

      const params = {
        filename: name === "" ? file.name : name,
        mime: fileType,
        extension: extension.toLowerCase(),
        organization: orgId,
        fileSize: file.size,
        MD5: MD5,
        Metadata: { MD5 },
      };

      return axios
        .post(apiUrls.medias.initializeMultipart, { data: params })
        .then((response) => {
          if (!response) throw response;
          // trim S3 path: it will help find the proper "currentUploadsStateList" item to update on 301_TRANSCODED_MEDIA event
          const s3name =
            response.data.S3filename.split("/")[response.data.S3filename.split("/").length - 1];
          dispatch(
            addUploadToCurrentList({
              s3name,
              uploadId: response.data.UploadId,
              name: name === "" ? file.name : name,
              percent: 0,
              errorMessage: null,
              isTranscoding: false,
              size: file.size,
              uploadState: "in_progress",
            }),
          );
          setTimeout(
            () =>
              putChunk({
                uploadId: response.data.UploadId,
                name: name === "" ? file.name : name,
                S3filename: response.data.S3filename,
                media360tag: media_360_tag,
                MD5,
                partNumber: 1,
                file,
                chunkBegining: 0,
                chunkEnd: chunkSizeInBytes,
                unsuccessfulTry: 0,
                cb,
                metadata,
              })(dispatch, getStore),
            0,
          );
          return response;
        })
        .catch((e: any) => {
          console.warn(e);
        });
    }
    return 1;
  };

export const askForPublicationOf3DEnvironment =
  ({ file, name }): any =>
  async () => {
    const params = {
      fileName: name === "" ? file.name : name,
    };

    let response;
    await axios
      .post(apiUrls.medias.askForPublicationOf3DEnvironment, { data: params })
      .then((res) => {
        response = res;
      })
      .catch((e: any) => {
        console.warn(e);
      });

    return response;
  };

const putChunk =
  ({
    uploadId,
    name,
    S3filename,
    media360tag,
    MD5,
    partNumber,
    file,
    chunkBegining,
    chunkEnd,
    unsuccessfulTry,
    cb,
    metadata = {},
  }: IPutChunkParams) =>
  (dispatch: Dispatch, getStore: any) => {
    const waitAfterUnsuccessfulInMs = 6000;
    const waitAfterSuccessfullInMs = 0;
    const chunkSizeInBytes = 10e6;
    let percent = 0;
    const uploadList: [] = getStore().media.currentUploadsStateList;
    const currUpload: any = uploadList.find((elt: IUploadItem) => elt.uploadId === uploadId);
    if (currUpload?.uploadState === "canceled") {
      return;
    }

    if (chunkEnd > file.size) {
      chunkEnd = file.size;
    }

    const getSignedUploadLinkData = {
      UploadId: uploadId,
      PartNumber: partNumber,
      S3filename: S3filename,
    };

    axios
      .post(apiUrls.medias.getSignedPartUploadLink, { data: getSignedUploadLinkData })
      .then((response) => {
        const chunk = file.slice(chunkBegining, chunkEnd);

        const axiosUploadInstance = axios.create();
        axiosUploadInstance.defaults.headers.common["Authorization"] = undefined;

        axiosUploadInstance
          .put(response.data, chunk)
          .then(() => {
            percent = (chunkEnd / file.size) * 100;
            unsuccessfulTry = 0;
            dispatch(
              addUploadToCurrentList({
                uploadId: uploadId,
                percent: percent,
              }),
            );

            if (chunkEnd === file.size) {
              const confirmData = {
                UploadId: uploadId,
                S3filename: S3filename,
              };

              axios
                .post(apiUrls.medias.completeMultipartUpload, { data: confirmData })
                .then((confirmResponse) => {
                  let fileType = file.type;
                  if (window.navigator.userAgent.includes("Safari")) {
                    const tabTmp = file.name.split(".");
                    const extension = tabTmp[tabTmp.length - 1];

                    if (
                      fileType === "" &&
                      (extension.toLowerCase() === "ogg" || extension.toLowerCase() === "oga")
                    ) {
                      fileType = "audio/" + extension.toLowerCase();
                    }
                  }

                  addMediaToDatabase({
                    name,
                    s3Url: confirmResponse.data,
                    mimeType: fileType,
                    media360tag,
                    fileSizeInByte: file.size,
                    fileName: S3filename,
                    cb,
                    dispatch,
                    uploadId,
                    MD5,
                    metadata,
                  });
                })
                .catch((e) => {
                  console.error("error => ", e);
                  throw e;
                });
            } else {
              setTimeout(
                () =>
                  putChunk({
                    uploadId,
                    name,
                    S3filename,
                    media360tag,
                    MD5,
                    partNumber: partNumber + 1,
                    file,
                    chunkBegining: chunkBegining + chunkSizeInBytes,
                    chunkEnd: chunkEnd + chunkSizeInBytes,
                    unsuccessfulTry: 0,
                    cb,
                  })(dispatch, getStore),
                waitAfterSuccessfullInMs,
              );
            }
          })
          .catch((e) => {
            console.error("error => ", e);
            unsuccessfulTry += 1;
            setTimeout(
              () =>
                putChunk({
                  uploadId,
                  name,
                  S3filename,
                  media360tag,
                  MD5,
                  partNumber,
                  file,
                  chunkBegining,
                  chunkEnd,
                  unsuccessfulTry,
                  cb,
                  dispatch,
                })(dispatch, getStore),
              waitAfterUnsuccessfulInMs,
            );
          });
      })
      .catch((e) => {
        console.error("error => ", e);
        dispatch(
          addUploadToCurrentList({
            uploadId: uploadId,
            percent: percent,
            errorMessage: e?.response?.data?.message ?? e,
            uploadState: "error",
          }),
        );
      });
  };

export const addMediaToDatabase = ({
  name,
  s3Url,
  mimeType,
  media360tag,
  fileSizeInByte,
  fileName,
  cb,
  dispatch,
  uploadId = "",
  MD5,
  tts,
  metadata = {},
}: {
  name: string;
  s3Url: string;
  mimeType: string;
  media360tag: string;
  fileSizeInByte: number;
  fileName: string;
  cb?: any;
  dispatch: Dispatch;
  uploadId: string;
  MD5: string;
  tts?: boolean;
  metadata?: Record<string, any>;
}) => {
  const uploadState = mimeType.includes("pdf") ? "transcribing" : "completed";

  const formData = {
    name: name,
    s3Url: s3Url,
    mimeType: mimeType,
    media360tag: media360tag,
    fileSizeInByte: fileSizeInByte,
    fileName: fileName,
    MD5: MD5,
    upload_status: uploadState,
    metadata,
  };

  return axios
    .post(apiUrls.medias.addMediaToDatabase, { data: formData })
    .then((response) => {
      if (cb) {
        cb(response.data);
      }
      if (!tts) {
        dispatch(
          addUploadToCurrentList({
            uploadId: uploadId,
            percent: 100,
            uploadState,
          }),
        );
      }

      dispatch(addMedia(response.data));
      return response.data;
    })
    .catch((e) => {
      console.error("error => ", e);
      dispatch(
        addUploadToCurrentList({
          uploadId: uploadId,
          percent: 100,
          errorMessage: e?.response?.data?.message ?? e,
          uploadState: "error",
        }),
      );
    });
};

/** This one is used to 'retry transcription'. */
export const transcribePdf = createAsyncThunk(
  "media/transcribePdf",
  async ({ mediaId }: { mediaId: number } & IAppLoaderAction) => {
    try {
      await axios.post(apiUrls.medias.transcribeMedia, { mediaId });
      return { mediaId };
    } catch (err: any) {
      console.error("transcribePdf error:", err);
      throw err?.response?.data?.error?.message ?? "Failed to start transcription";
    }
  },
);

export const addBlockadeImageToDatabase = ({
  name,
  imageUrl,
  thumbnailUrl,
  fileSizeInByte,
  fileName,
  cb,
  dispatch,
  prompt,
  MD5,
}: {
  name: string;
  imageUrl: string;
  thumbnailUrl: string;
  fileSizeInByte: number;
  fileName: string;
  cb?: any;
  dispatch: Dispatch;
  prompt: string;
  MD5: string;
}) => {
  const uploadState = "completed";

  const formData = {
    name: name,
    imageUrl: imageUrl,
    thumbnailUrl: thumbnailUrl,
    fileSizeInByte: fileSizeInByte,
    fileName: fileName,
    MD5: MD5,
    upload_status: uploadState,
    prompt,
  };

  return axios
    .post(apiUrls.medias.blockadeLabs.addBlockadeImageToDatabase, { data: formData })
    .then((response) => {
      if (cb) {
        cb(response.data);
      }
      dispatch(
        addUploadToCurrentList({
          uploadId: uuidv4(),
          name,
          percent: 100,
          uploadState,
        }),
      );

      dispatch(addMedia(response.data));
      return response.data;
    })
    .catch((e) => {
      console.error("error => ", e);
      dispatch(
        addUploadToCurrentList({
          uploadId: uuidv4(),
          percent: 100,
          name,
          errorMessage: e?.response?.data?.message ?? e,
          uploadState: "error",
        }),
      );
    });
};

export const fetchBlockadeLabsImageModels = createAsyncThunk(
  "media/fetchBlockadeLabsModels",
  // eslint-disable-next-line no-empty-pattern
  async ({}: any & IAppLoaderAction) => {
    return await axios.get(`${apiUrls.medias.blockadeLabs.models}`).then((res) => res.data);
  },
);

export const generateBlockadeLabsImagePreview = createAsyncThunk(
  "media/generateBlockadeLabsImage",
  async ({
    modelId,
    prompt,
    imagePrompt = undefined,
    initStrength = undefined,
    sourceMode = undefined,
  }: IBlockadeLabsPreviewPayload & IAppLoaderAction) => {
    try {
      const response = await axios.post(apiUrls.medias.blockadeLabs.generate, {
        skybox_style_id: modelId,
        prompt,
        imagePrompt,
        initStrength,
        sourceMode,
      });
      return response.data;
    } catch (e: any) {
      console.error("Error in generateBlockadeLabsImagePreview: ", e);
      const message =
        e?.data?.error?.message || // interceptor-transformed response object
        e?.response?.data?.error?.message || // standard AxiosError
        e?.message ||
        "Unknown error during image generation";
      throw new Error(message);
    }
  },
);

export const mediaSlice = createSlice({
  name: "media",
  initialState,
  extraReducers: (builder) => {
    builder.addCase(getAllMedia.pending, (state) => {
      state.isGalleryLoaded = false;
    });

    builder.addCase(fetchFilteredMedias.pending, (state) => {
      // Do NOT touch isGalleryLoaded here: it tracks getAllMedia (which fills
      // `all`, the only array Avatar3dPopup reads), while this thunk fills
      // `filtered`. Clobbering it to false — with no fulfilled/rejected reset —
      // permanently pinned the flag false after the session's first filtered
      // fetch, leaving the profile avatar picker spinning forever. The
      // filteredMediasInFlight counter (below) is what tracks this thunk.
      state.filteredMediasInFlight += 1;
    });

    builder.addCase(fetchFilteredMedias.fulfilled, (state, { payload }) => {
      state.filtered = payload.data.results as Array<IMedia>;
      state.numberOfSearchHits = Number(payload.data.totalResults);
      state.search.selectedId = null;
      state.filteredMediasInFlight = Math.max(0, state.filteredMediasInFlight - 1);
    });

    builder.addCase(fetchFilteredMedias.rejected, (state) => {
      state.filteredMediasInFlight = Math.max(0, state.filteredMediasInFlight - 1);
    });

    builder.addCase(getAllMedia.fulfilled, (state, { payload }) => {
      const mediaList = payload.data as Array<IMedia>;
      state.config.tags = getTagsOrganizationMedia(payload.tagsOrganization, payload.orgId);
      state.config.mediaType = getMediaTypeValue(mediaList);
      state.config.labels = getMediaLabelValue(mediaList);
      state.all = mediaList;
      state.isGalleryLoaded = true;
    });
    builder.addCase(getAllMedia.rejected, (state, { error }) => {
      // Leave the loading state on failure (was stuck false before) and surface it.
      state.isGalleryLoaded = true;
      console.error("getAllMedia failed:", error?.message);
    });
    builder.addCase(updateMediaDetail.fulfilled, (state, { payload }) => {
      // Keep both arrays in sync (same rationale as the deleteMedia cases
      // below): the gallery grid and currentMediaSelector read `filtered`,
      // so patching only `all` leaves a renamed paginated/AI-generated media
      // showing its stale name even though the backend PUT succeeded.
      state.all = [...state.all].map((cur: IMedia) => (cur.id === payload.id ? payload : cur));
      state.filtered = [...state.filtered].map((cur: IMedia) =>
        cur.id === payload.id ? payload : cur,
      );
    });
    builder.addCase(updateAvatarVoices.fulfilled, (state, { payload }) => {
      state.all = [...state.all].map((cur: IMedia) =>
        cur.id === payload.id ? { ...cur, metadata: payload.metadata } : cur,
      );
      state.filtered = [...state.filtered].map((cur: IMedia) =>
        cur.id === payload.id ? { ...cur, metadata: payload.metadata } : cur,
      );
    });
    builder.addCase(getMedia.fulfilled, (state, { payload }) => {
      if (Object.prototype.hasOwnProperty.call(payload, "id")) {
        state.all = [...state.all].map((cur: IMedia) =>
          Number(cur.id) === Number(payload.id) ? payload : cur,
        );
        state.filtered = [...state.filtered].map((cur: IMedia) =>
          Number(cur.id) === Number(payload.id) ? payload : cur,
        );
      }
    });
    builder.addCase(deleteMedia.fulfilled, (state, { payload }) => {
      // remove from gallery store
      state.all = [...state.all].filter((cur: IMedia) => {
        return Number(cur.id) !== Number(payload.deletedMediaId);
      });
      state.filtered = [...state.filtered].filter((cur: IMedia) => {
        return Number(cur.id) !== Number(payload.deletedMediaId);
      });
    });
    builder.addCase(deleteMultipleMediaFromIds.fulfilled, (state, { payload }) => {
      // remove from gallery store — one Set instead of rescanning the deleted
      // ids for every media of both lists
      // NaN is dropped on purpose: `===` never matched it, but Set.has would.
      const deletedIds = new Set(
        payload.map((elt) => Number(elt.deletedMediaId)).filter((id) => !Number.isNaN(id)),
      );
      state.all = [...state.all].filter((cur: IMedia) => !deletedIds.has(Number(cur.id)));
      state.filtered = [...state.filtered].filter((cur: IMedia) => !deletedIds.has(Number(cur.id)));
    });
    builder.addCase(generateVoiceOver.fulfilled, (state, { payload }) => {
      const createBlobUrl = (audioBuffer: ArrayBuffer) => {
        const blob = new Blob([audioBuffer], { type: "audio/mpeg" });
        return URL.createObjectURL(blob);
      };

      const blobUrl = createBlobUrl(payload);
      state.ttsPreview = { url: blobUrl };
    });
    builder.addCase(fetchElevenlabsVoices.fulfilled, (state, { payload }) => {
      state.elevenlabsVoices = payload.slice().sort((a, b) => a.name.localeCompare(b.name));
    });
    // updates of the store are done in deleteMedia.then(...)
    // (it was too painful to dispatch in other slices from here)
    builder.addCase(fetchBlockadeLabsImageModels.fulfilled, (state, { payload }) => {
      state.blockadeLabs.models = payload;
    });
    builder.addCase(generateBlockadeLabsImagePreview.fulfilled, (state, { payload }) => {
      state.blockadeLabs.preview = payload;
    });
    builder.addCase(generateBlockadeLabsImagePreview.rejected, (state) => {
      state.blockadeLabs.preview = null;
    });
    builder.addCase(generateVoiceOver.rejected, (state) => {
      state.ttsPreview = null;
    });
    builder.addCase(fetchMediaSearchIndex.fulfilled, (state, { payload }) => {
      state.searchIndex = payload;
    });
    builder.addCase(transcribePdf.fulfilled, (state, { payload }) => {
      const updated = state.all.map((media) => {
        if (media.id === payload.mediaId) {
          return {
            ...media,
            upload_status: "transcribing",
          };
        }
        return media;
      });
      state.all = updated;

      const media = updated.find((m) => m.id === payload.mediaId);
      const alreadyInUploadList = state.currentUploadsStateList.some(
        (upload) => upload.s3name === media?.filename,
      );

      if (!alreadyInUploadList && media) {
        state.currentUploadsStateList.push({
          uploadId: uuidv4(),
          name: media.name,
          s3name: media.filename,
          size: media.size_in_bytes,
          uploadState: "transcribing",
          percent: 100,
        });
      } else {
        state.currentUploadsStateList = state.currentUploadsStateList.map((upload) => {
          if (upload.s3name === media?.filename) {
            return {
              ...upload,
              uploadState: "transcribing",
            };
          }
          return upload;
        });
      }
    });
  },
  reducers: {
    setCurrentMediaSidebar: (state: IMediaState, action: PayloadAction<number>) => {
      state.currentMediaSidebar = action.payload;
    },
    setCurrentMediaGallery: (state: IMediaState, action: PayloadAction<number>) => {
      state.currentMediaGallery = action.payload;
    },
    setOriginalMediaGallery: (state: IMediaState, action: PayloadAction<number>) => {
      state.originalMediaGallery = action.payload;
    },
    setUploadedFile: (state: IMediaState, action: PayloadAction<ISUFPayload | undefined>) => {
      if (action.payload) {
        state.uploadedFile = action.payload.file;
        state.uploadedFileMetadata = action.payload.metadata;
      } else {
        state.uploadedFile = undefined;
        state.uploadedFileMetadata = undefined;
      }
    },
    setUploadedOldFile: (state: IMediaState, action: PayloadAction<ISUFPayload | undefined>) => {
      if (action.payload) {
        state.uploadedOldFile = action.payload.file;
        state.uploadedOldFileMetadata = action.payload.metadata;
      } else {
        state.uploadedOldFile = undefined;
        state.uploadedOldFileMetadata = undefined;
      }
    },
    setCurrentModeListGallery: (state: IMediaState, action: PayloadAction<boolean>) => {
      state.currentModeListGallery = action.payload;
    },
    //filters state

    setUploadNewTagsInSuggestions: (
      state: IMediaState,
      action: PayloadAction<UploadStateConfigTags>,
    ) => {
      const tagsInStore = action.payload.oldTags;
      const newTag = action.payload.newTags;
      const tagsNewArray = tagsInStore.concat(newTag);
      const uniqueIds: any[] = [];

      const uniqueTags = tagsNewArray.filter((element: any) => {
        const isDuplicate = uniqueIds.includes(element.id);
        if (!isDuplicate) {
          uniqueIds.push(element.id);
          return true;
        }
        return false;
      });
      state.config.tags = uniqueTags;
    },
    addMedia: (state: IMediaState, action: PayloadAction<IMedia>) => {
      state.all.push(action.payload);
      state.filtered.unshift(action.payload);
      state.shouldResetFilters = true;
      state.activeArrowFilterList = "";
    },
    addNewTags: (state: any, action: PayloadAction<[]>) => {
      state.media.config.iTags.push(action.payload);
    },
    setActiveArrowFilterList: (state: any, action: PayloadAction<string>) => {
      state.activeArrowFilterList = action.payload;
    },
    addUploadToCurrentList: (state: IMediaState, action: PayloadAction<IUploadItem>) => {
      if (!state.currentUploadsStateList) {
        state.currentUploadsStateList = [];
      }
      const index = state.currentUploadsStateList.findIndex((e) => {
        return e.uploadId === action.payload.uploadId;
      });
      if (index === -1) {
        state.currentUploadsStateList.push(action.payload);
      } else {
        Object.entries(action.payload).forEach((kv: any) => {
          (state.currentUploadsStateList[index] as any)[kv[0]] = kv[1];
        });
      }
    },
    setUploadCurrentList: (state, action) => {
      state.currentUploadsStateList = action.payload;
    },

    setCurrentButtonModalGalleryConfirm: (state, action) => {
      state.activeButtonModalGalleryConfirm = action.payload;
    },
    clearGallery: (state: IMediaState) => {
      (Object.keys(state) as Array<keyof typeof state>).forEach((key) => {
        (state[key] as any) = initialState[key];
      });
    },
    setOpenedSlider: (state, action) => {
      state.openedSlider = action.payload;
    },
    resetTtsPreview: (state) => {
      state.ttsPreview = null;
    },
    setBlockadeLabsImagePreview: (state, action) => {
      if (Number(action.payload.blockadeLabImageId) === Number(state.blockadeLabs.preview?.id)) {
        state.blockadeLabs.preview = action.payload;
      }
    },
    resetBlockadeLabsImagePreview: (state) => {
      state.blockadeLabs.preview = null;
    },

    setMediaSearch: (state: IMediaState, action: PayloadAction<SearchUpdatePayload>) => {
      if (isSingleSearchUpdate(action.payload)) {
        const { key, value } = action.payload;
        (state.search as any)[key] = value;
        if (key === "selectedId") {
          state.search.page = 0;
          state.search.sort = "updated_at_desc";
          state.search.media360tag = "";
          state.search.mediaType = "all";
          state.search.owner = "all";
        }
      } else {
        Object.assign(state.search, action.payload);
      }
    },

    resetMediaSearch: {
      reducer(state: IMediaState, action: PayloadAction<{ resetEverything?: boolean }>) {
        const defaultSearch = {
          limit: 20,
          page: 0,
          query: "",
          selectedId: "",
          sort: "updated_at_desc",
          media360tag: "",
          mediaType: "all",
          owner: "all",
        };

        if (action.payload.resetEverything) {
          // full reset
          state.search = { ...defaultSearch };
        } else {
          // partial reset — keep projectId & projectGroupId ~
          state.search = {
            ...state.search,
            limit: 20,
            page: 0,
            query: "",
            selectedId: "",
            sort: "updated_at_desc",
            media360tag: "",
            mediaType: "all",
            owner: "all",
          };
        }
      },
      // makes the payload optional
      prepare: (payload?: { resetEverything?: boolean }) => ({
        payload: payload ?? {},
      }),
    },
    /* ETA 18 sept 2023: this is deprecated 
    setUploadHasBeenTranscoded: (state, action) => {
     // We need to update state.all to stop hiding the relevant media and
    //  we need to update state.currentUploadsStateList to display "completed" & allow to empty the upload list
      
const updatedState = state.all.map((media: IMedia) => {
	if (Number(media.id) === Number(action.payload)) {
		state.currentUploadsStateList = [...state.currentUploadsStateList].map(
			(currentUpload: IUploadItem) => {
				if (media.filename === currentUpload.s3name) {
					return {
						...currentUpload,
                  uploadState: "completed",
                };
              } else {
								return currentUpload;
              }
            },
						);
						return {
							...media,
							upload_status: "completed",
						};
        } else {
					return media;
        }
      });
      state.all = updatedState;
    },*/

    setUploadHasBeenTranscribed: (state, action) => {
      // We need to update state.all to stop hiding the relevant media and
      // we need to update state.currentUploadsStateList to display "completed" & allow to empty the upload list
      const updatedState = state.all.map((media: IMedia) => {
        if (Number(media.id) === Number(action.payload)) {
          state.currentUploadsStateList = [...state.currentUploadsStateList].map(
            (currentUpload: IUploadItem) => {
              if (media.filename === currentUpload.s3name) {
                return {
                  ...currentUpload,
                  uploadState: "completed",
                };
              } else {
                return currentUpload;
              }
            },
          );
          return {
            ...media,
            upload_status: "completed",
          };
        } else {
          return media;
        }
      });
      state.all = updatedState;
    },
  },
});

export const mediaConfigSelector = (state: RootState) => state.media.config;

export const mediaSelector = (state: RootState) => state.media.all;
export const getFilteredMedias = (state: RootState) => state.media.filtered;

export const mediaByProjectSelector = (state: RootState): Array<IMedia> => {
  //@todo don't use graphLegacy
  const mediaIdForCurrentProject = state.graphLegacy.sourceMedias.ids;
  const allMedia = mediaSelector(state);
  return allMedia.filter((media: IMedia) => {
    if (!media?.id) {
      console.log("Omagad... !media.id in mediaByProjectSelector 😱.. media=", media);
    }
    return mediaIdForCurrentProject.includes(media.id);
  });
};

export const isUploading = (state: RootState) => state.media.isUploading;
export const currentMediaSelector = (state: RootState) => {
  // Dual lookup, same rationale as getCurrentMediaGallery below: the gallery
  // grid is rendered from `filtered` (paginated/search results), so a media
  // the user can click "edit" on may not exist in the app-boot `all`
  // snapshot — e.g. recently uploaded items or AI-generated skyboxes
  // reachable only through paginated search. Without searching `filtered`
  // first, currentMediaSelector returns undefined, MediaDetailContainer
  // renders nothing, and the edit SlideOver never mounts (the click still
  // dispatches openSlider + setCurrentMediaSidebar — it just no-ops).
  const id = Number(state.media.currentMediaSidebar);
  return (
    state.media.filtered.find((media: IMedia) => Number(media.id) === id) ||
    state.media.all.find((media: IMedia) => Number(media.id) === id)
  );
};
export const getCurrentEditedMediaField = (state: RootState) => state.media.currentEditedMediaField;
export const getCurrentMediaGallery = (state: RootState) => {
  // Search the picker's actual data source first (filtered = paginated
  // results from the gallery API, populated as the user browses pages).
  // Fall back to `all` for legacy callers that landed media there via the
  // app-boot `getAllMedia` snapshot. Without this dual lookup the picker's
  // Confirm button silently no-ops on any media that wasn't in the initial
  // org-wide snapshot — e.g. recently uploaded items or anything reachable
  // only through paginated search.
  const id = Number(state.media.currentMediaGallery);
  return (
    state.media.filtered.find((media: IMedia) => Number(media.id) === id) ||
    state.media.all.find((media: IMedia) => Number(media.id) === id)
  );
};
export const getCurrentMediaGalleryId = (state: RootState) => state.media.currentMediaGallery;
export const getOriginalMediaGalleryId = (state: RootState) => state.media.originalMediaGallery;
export const getUploadedFile = (state: RootState) => state.media.uploadedFile;
export const getUploadedOldFile = (state: RootState) => state.media.uploadedOldFile;
export const getUploadedFileMetadata = (state: RootState) => state.media.uploadedFileMetadata;
export const getCurrentUploadsStateList = (state: RootState) => state.media.currentUploadsStateList;
export const getCurrentTagsOrganizationId = (state: RootState) => state.media.config.tags;
export const getOpenedSlider = (state: RootState) => state.media.openedSlider;
export const getIsGalleryLoaded = (state: RootState) => state.media.isGalleryLoaded;
export const getIsFilteredMediasLoading = (state: RootState) =>
  state.media.filteredMediasInFlight > 0;
export const mediaReducer = mediaSlice.reducer;
export const getCurrentModeListGallery = (state: RootState) => state.media.currentModeListGallery;
export const getElevenlabsVoices = (state: RootState) => state.media.elevenlabsVoices;
export const getTtsPreview = (state: RootState) => state.media.ttsPreview;
export const getBlockadeLabsModels = (state: RootState) => state.media.blockadeLabs.models;
export const getBlockadeLabsPreview = (state: RootState) => state.media.blockadeLabs.preview;
export const getMediaSearch = (state: RootState) => state.media.search;
export const getMediaSearchIndex = (state: RootState) => state.media.searchIndex;
export const getSearchLimit = (state: RootState) => state.media.search.limit;
export const getNumberOfMediaSearchHits = (state: RootState) => state.media.numberOfSearchHits;
export const getPreferredElevenLabsVoice = (state: RootState) => {
  const prefs = getUserPreferences({ currentUser: state.profile.currentUser }) ?? {};
  const favorite = prefs.favoriteVoiceId;

  const foundVoice = state.media.elevenlabsVoices.find((voice) => voice.id === favorite);
  if (favorite && foundVoice) {
    return {
      voiceId: favorite,
      ttsProvider: foundVoice.tts_provider,
    };
  }

  const output = state.media.elevenlabsVoices[0]?.id
    ? {
        voiceId: state.media.elevenlabsVoices[0]?.id,
        ttsProvider: state.media.elevenlabsVoices[0]?.tts_provider,
      }
    : HARDCODED_ELEVENLABS_VOICE_ID;

  return output;
};

export const {
  addMedia,
  setCurrentModeListGallery,
  setCurrentMediaSidebar,
  setCurrentMediaGallery,
  setOriginalMediaGallery,
  setCurrentButtonModalGalleryConfirm,
  setUploadedFile,
  setUploadedOldFile,
  setUploadNewTagsInSuggestions,
  addUploadToCurrentList,
  setUploadCurrentList,
  clearGallery,
  setOpenedSlider,
  setMediaSearch,
  resetMediaSearch,
  setActiveArrowFilterList,
  resetTtsPreview,
  setBlockadeLabsImagePreview,
  resetBlockadeLabsImagePreview,
  // setUploadHasBeenTranscoded,
  setUploadHasBeenTranscribed,
} = mediaSlice.actions;
